import { z } from "zod";
import { paginationMetaDtoSchema } from "@nasam/kernel";

const required = z.string().trim().min(1);

const namedRefDtoSchema = z.object({
  id: required,
  nameEn: z.string(),
  nameAr: z.string(),
});

export const alertPriorityDtoSchema = z.enum(["VERY_HIGH", "HIGH", "MEDIUM", "LOW"]);
export const alertStatusDtoSchema = z.enum(["ACTIVE", "INACTIVE"]);
export const alertTargetTypeDtoSchema = z.enum([
  "ASSET_TYPE",
  "ASSET",
  "DEVICE_TYPE",
  "DEVICE",
]);
export const alertDayDtoSchema = z.enum([
  "SATURDAY",
  "SUNDAY",
  "MONDAY",
  "TUESDAY",
  "WEDNESDAY",
  "THURSDAY",
  "FRIDAY",
]);

export const alertTriggerKindDtoSchema = z.enum([
  "MAX_SPEED",
  "DOWNTIME",
  "TEMPERATURE",
  "RANGE",
  "BINARY",
  "THRESHOLD",
  "DRIVER_BEHAVIOUR",
]);

// The API calls it `triggerType` (it was `triggerKind`); the app keeps
// `triggerKind`.
export const alertCategoryDtoSchema = namedRefDtoSchema
  .extend({
    code: required,
    triggerType: alertTriggerKindDtoSchema,
    sensorType: z.string().nullable(),
  })
  .transform(({ triggerType, ...category }) => ({
    ...category,
    triggerKind: triggerType,
  }));

// A category the app can't read yet (a trigger type added on the API) is
// left out of the picker rather than failing the whole list.
export const alertCategoriesResponseDtoSchema = z.object({
  data: z.array(z.unknown()).transform((items) =>
    items.flatMap((item) => {
      const category = alertCategoryDtoSchema.safeParse(item);
      return category.success ? [category.data] : [];
    }),
  ),
});

export const alertRecipientDtoSchema = namedRefDtoSchema.extend({
  username: z.string(),
  email: z.string(),
});

export const alertRecipientsResponseDtoSchema = z.object({
  data: z.array(alertRecipientDtoSchema),
});

// Each field is present only for the category's own trigger kind.
export const alertTriggerDtoSchema = z.object({
  maxSpeed: z.number().nullish(),
  downtimeMinutes: z.number().nullish(),
  minTemperature: z.number().nullish(),
  maxTemperature: z.number().nullish(),
  minHumidity: z.number().nullish(),
  maxHumidity: z.number().nullish(),
  minValue: z.number().nullish(),
  maxValue: z.number().nullish(),
  alertValue: z.boolean().nullish(),
  sensorType: z.string().nullish(),
  operator: z.enum(["GREATER_THAN", "GREATER_THAN_OR_EQUAL", "LESS_THAN", "LESS_THAN_OR_EQUAL", "EQUAL", "BETWEEN"]).nullish(),
  thresholdValue: z.number().nullish(),
  thresholdValueTo: z.number().nullish(),
  unit: z.string().nullish(),
});

export const alertTargetDtoSchema = z.object({
  id: required,
  assetType: namedRefDtoSchema.nullish(),
  asset: namedRefDtoSchema.extend({ code: z.string().nullish() }).nullish(),
  deviceType: namedRefDtoSchema.nullish(),
  // Not shown in the contract's examples -- read leniently.
  device: z
    .object({
      id: required,
      imei: z.string().nullish(),
      nameEn: z.string().nullish(),
      nameAr: z.string().nullish(),
    })
    .nullish(),
});

export const alertListItemDtoSchema = z.object({
  id: required,
  nameEn: z.string(),
  nameAr: z.string(),
  priority: alertPriorityDtoSchema,
  status: alertStatusDtoSchema,
  targetType: alertTargetTypeDtoSchema,
  createdAt: z.string(),
  updatedAt: z.string(),
  account: namedRefDtoSchema,
  category: alertCategoryDtoSchema,
});

export const alertsListResponseDtoSchema = z.object({
  data: z.array(alertListItemDtoSchema),
  meta: paginationMetaDtoSchema,
});

export const alertDetailDtoSchema = alertListItemDtoSchema.extend({
  description: z.string().nullish(),
  accountId: required,
  trigger: alertTriggerDtoSchema.nullish(),
  channels: z.array(z.enum(["SYSTEM", "EMAIL"])).nullish(),
  alwaysActive: z.boolean(),
  activeDays: z.array(alertDayDtoSchema).nullish(),
  repeatWeekly: z.boolean().nullish(),
  startTime: z.string().nullish(),
  endTime: z.string().nullish(),
  targets: z.array(alertTargetDtoSchema).nullish(),
  recipients: z.array(z.object({ user: alertRecipientDtoSchema })).nullish(),
  targetIds: z.array(z.string()).nullish(),
  recipientIds: z.array(z.string()).nullish(),
});

export const alertDetailResponseDtoSchema = z.object({
  data: alertDetailDtoSchema,
});

export const alertSavedResponseDtoSchema = z.object({
  message: z.string().optional(),
  data: z.object({ id: required }),
});

export const alertDeleteResponseDtoSchema = z.object({
  message: z.string().optional(),
});

export type AlertListItemDto = z.infer<typeof alertListItemDtoSchema>;
export type AlertDetailDto = z.infer<typeof alertDetailDtoSchema>;
export type AlertTriggerDto = z.infer<typeof alertTriggerDtoSchema>;
export type AlertTargetDto = z.infer<typeof alertTargetDtoSchema>;
